import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { ReportStandardGroupViewModel, ReportService } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { Utilities } from '../../../services/utilities';

@Component({
    selector: 'app-report-standard-group-details',
    templateUrl: './report-standard-group-details.component.html',
    styleUrls: ['./report-standard-group-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportStandardGroupDetailsComponent implements OnInit {
    @ViewChild('form', { static: true }) form: NgForm;

    reportStandardGroupForm: UntypedFormGroup;

    reportStandardGroupId: number;
    reportStandardGroupName: string;
    reportId: number;

    loadingIndicator: boolean = false;
    private isSaving: boolean = false;
    anySaveOperationsPerformed: boolean = false;
    hasPermissionToChange$: Observable<boolean>;

    constructor(
        public dialogRef: MatDialogRef<ReportStandardGroupDetailsComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private reportService: ReportService,
        private formBuilder: UntypedFormBuilder,
        private snackBar: MatSnackBar,
        public dialog: MatDialog
    ) {
        this.reportStandardGroupId = data.reportStandardGroupId;
        this.reportStandardGroupName = data.reportStandardGroupName;
        this.reportId = data.reportId;
        this.hasPermissionToChange$ = data.hasPermissionToChange$;
    }

    ngOnInit(): void {
        this.buildForm();
        this.loadData(this.reportStandardGroupId);

        // Use this construct to handle the escape key for cancelling
        this.dialogRef.keydownEvents().pipe(map(r => new EventMapper(r, this))).subscribe((e) => {
            if (e.event.key === "Escape") {
                e.component.cancel();
            }
        }
        );

    }

    public loadData(reportStandardGroupId: number): void {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;
        this.reportService.getReportStandardGroupByReportStandardGroupId(reportStandardGroupId).subscribe(ok => this.dataLoaded(ok),
            error => this.onDataLoadFailed(error));
    }

    dataLoaded(reportStandardGroup: ReportStandardGroupViewModel) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.resetForm(reportStandardGroup);
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"),
            this.translationService.getTranslation("FetchErrorMessage") +
            "\r\n" +
            Utilities.getHttpResponseMessage(error),
            MessageSeverity.error,
            error);
    }


    public getFormControl(name: string): AbstractControl {
        return this.reportStandardGroupForm.controls[name];
    }

    private buildForm() {
        this.reportStandardGroupForm = this.formBuilder.group({
            reportStandardGroupId: '',
            name: '',
            reportId: '',
            comment: ''
        });
    }

    public resetForm(reportStandardGroup: ReportStandardGroupViewModel) {
        this.reportStandardGroupForm.reset({
            reportStandardGroupId: reportStandardGroup.reportStandardGroupId,
            name: reportStandardGroup.name,
            reportId: reportStandardGroup.reportId,
            comment: reportStandardGroup.comment,
        });
    }

    private prepareSave(): ReportStandardGroupViewModel {
        const formModel = this.reportStandardGroupForm.value;

        return {
            reportStandardGroupId: formModel.reportStandardGroupId,
            comment: formModel.comment,
            reportId: formModel.reportId
        };
    }

    public save(close: boolean) {
        if (!this.reportStandardGroupForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const reportStandardGroup = this.prepareSave();

        this.reportService.updateReportStandardGroup(reportStandardGroup).subscribe(
            t => this.saveCompleted(close),
            error => this.saveFailed(error));
    }

    public cancel() {
        this.alertService.resetStickyMessage();

        if (this.reportStandardGroupForm.dirty) {
            this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 })
                .onAction().subscribe(() => {
                    this.dialogRef.close(this.anySaveOperationsPerformed);
                }
                );
        } else {
            this.dialogRef.close(this.anySaveOperationsPerformed);
        }
    }

    private saveCompleted(close: boolean) {

        this.isSaving = false;
        this.anySaveOperationsPerformed = true;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), this.reportStandardGroupName + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        if (close) {
            this.dialogRef.close(true);
        } else {
            //this.resetForm(reportStandardGroup);
        }
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.changeReports);
    }

}

export class EventMapper {
    component: ReportStandardGroupDetailsComponent;
    event: KeyboardEvent;

    constructor(event: KeyboardEvent, component: ReportStandardGroupDetailsComponent) {
        this.component = component;
        this.event = event;
    }
}

